// cleanupChain.mjs — SB-146 直登清欠链的纯逻辑（第一百二十二批）。
// 无 React、无 DOM：入链判定 / 链构建 / 推进与完成判定，全部输入化（SB-042 惯例）。
// 组件（ForcedReadGate.jsx 的清欠段）只是薄壳。
//
// ── 清欠模式是什么（SB-146 §2 裁决 4）──────────────────────────────────────
// 直登（邮件链接 login_link）用户看完目标页即走，铃铛通知永远未读。所以：
// sessionOrigin === 'login_link' 且存在任何未读（站内信+铃铛）⇒ 站内信先走强读闸
// （forcedReadQueue.mjs），然后铃铛未读**旧→新逐条**：跳到该通知的目标页 + 缘由
// modal + 标已读；尝试离开 ⇒ 跳下一条；全清 ⇒ 告知恢复自由。
//
// ── 状态只在内存（会话内）──────────────────────────────────────────────────
// 链一旦建好就在组件 state 里推进；刷新后按服务端未读现状**重建**。刻意不落
// localStorage：服务端未读数才是唯一事实——一份本地进度既可能把已在别处读掉的
// 通知再拦一遍，也可能把新到的漏掉，两个方向都是假话。
import { targetOf } from './notifTarget.mjs';

// 未读通知一次拉多少条。**有上限**这件事本身就是缺陷源：一轮链走完只说明这一页
// 清完了，不是欠账清零——所以清欠段与强读闸同走 drainRounds（forcedReadQueue.mjs），
// 拉回 0 条才算全清。数值写在这里，不散落在调用处（抄形状不抄数值）。
export const NOTIF_UNREAD_PAGE_LIMIT = 100;

// ── 入链判定 ────────────────────────────────────────────────────────────────
// **只有 'login_link' 进清欠**——'code' / 'password' / null（旧会话行）/ undefined
// （老后端还没回这个字段）一律不进。失败方向：字段缺席 ⇒ 不清欠（少清一次），
// 绝不把普通登录当直登拦住（多拦是把裁决 4 的边界私自扩大）。
export function needsCleanup(sessionOrigin, dmUnread, notifUnread) {
  if (sessionOrigin !== 'login_link') return false;
  return Number(dmUnread) > 0 || Number(notifUnread) > 0;
}

// ── 链构建：未读铃铛通知 → 步骤，旧→新 ─────────────────────────────────────
// 输入是 GET /api/me/notifications 响应里的 notifications 数组（原始行）。
// 目标页判定**只走 notifTarget.mjs 的 targetOf**——那是「点这条通知去哪」的唯一
// 判据（第一百二十一批刚扩过），这里再写一份就是第二份会漂的路由清单。
// target 为 null 的终局类通知（如 backlog_cancelled：没有任何面还能显示它的对象）
// 留在链里但无处可跳：清欠仍要把它标已读，否则服务端未读数永不清零，
// 下一次直登会永远重新进入清欠。
export function buildChain(notifications) {
  const rows = Array.isArray(notifications) ? notifications : [];
  const steps = [];
  for (const n of rows) {
    if (!n || typeof n !== 'object' || n.id == null) continue;
    if (n.readAtMs) continue;                    // 只清未读（与铃铛行的判读同形）
    const target = targetOf(n);
    const created = Number(n.createdAtMs);
    steps.push({
      id: String(n.id),                          // 与铃铛/接口一致收成字符串
      kind: typeof n.kind === 'string' ? n.kind : '',
      createdAtMs: Number.isFinite(created) ? created : 0,
      target,                                    // 完整跳转目标（可带 query）或 null
      targetPath: pathOf(target),                // 「离开判定」用的 pathname 部分
    });
  }
  // 旧→新：欠得最久的先还（与强读队列同一条顺序纪律）；同刻按数值 id 钉稳。
  steps.sort((a, b) => a.createdAtMs - b.createdAtMs || Number(a.id) - Number(b.id));
  return steps;
}

// 跳转目标的 pathname 部分。「离开当前通知页」按 pathname 判，不按整串判：
// /messages?box=inbox&focus=7 页里切个 tab 改的是组件 state 不是路由，而万一哪天
// 有页面把状态写进 query，改 query 也不该算「离开」——用户还在那一页上。
export function pathOf(target) {
  if (typeof target !== 'string' || target === '') return null;
  const i = target.indexOf('?');
  return i < 0 ? target : target.slice(0, i);
}

// 链走完了吗。idx 是**下一步**的下标：等于长度即全清。
export function isDone(chain, idx) {
  return !Array.isArray(chain) || idx >= chain.length;
}

// 用户离开了当前通知页吗（路由变更时问）。无目标页的步骤（target null）无从
// 「离开」⇒ 恒 false：它的推进走 modal 确认那条路，不走路由监听。
export function leftStep(pathname, step) {
  if (!step || step.targetPath == null) return false;
  return pathname !== step.targetPath;
}
